import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { CalendarRange, CheckCircle2, Clock, TrendingUp } from "lucide-react";
import { useState } from "react";
import { CronFailureAlert } from "./components/cron-failure-alert";
import { CycleBanner } from "./components/cycle-banner";
import { DateRangeInputs } from "./components/date-range-inputs";
import { ExportTablesMenu } from "./components/export-tables-menu";
import { PendingActions } from "./components/pending-actions";
import { ShippingHistory } from "./components/shipping-history";
import { SummaryCards } from "./components/summary-cards";
import {
  APPROVED_HIGHLIGHT_CLASS_NAME,
  CASES_DIFF_CLASS_NAME,
  EMPTY_RANGE,
} from "./constants";
import {
  useGetDashboardCycleSummaryQuery,
  useGetDashboardPendingActionsQuery,
} from "@/services/conciliation-dashboard";
import { useDashboardExports } from "./hooks/use-dashboard-exports";
import { DASHBOARD_LABELS } from "./messages";
import { DateRange } from "./types";

const DashboardConciliacao = (): JSX.Element => {
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);

  const exports = useDashboardExports(range);

  const { data: cycle } = useGetDashboardCycleSummaryQuery(range);
  const { data: pending } = useGetDashboardPendingActionsQuery();

  const processed: number = cycle?.processed ?? 0;
  const previousProcessed: number = cycle?.previous_processed ?? 0;
  const casesDiff: number = processed - previousProcessed;

  return (
    <PageLayout>
      <div className="flex w-full flex-1 flex-wrap items-start justify-between gap-3">
        <PageTitle
          title={DASHBOARD_LABELS.PAGE_TITLE}
          description={DASHBOARD_LABELS.PAGE_DESCRIPTION}
        />
        <div className="cursor-pointer">
          <DateRangeInputs value={range} onChange={setRange} />
        </div>
      </div>

      <ContentContainer className="space-y-6">
        {pending?.cron_failure && <CronFailureAlert />}

        <CycleBanner
          items={[
            {
              label: DASHBOARD_LABELS.CYCLE_OPEN,
              value: cycle?.interval ?? "",
              Icon: CalendarRange,
            },
            {
              label: DASHBOARD_LABELS.DAYS_TO_CLOSE,
              value: DASHBOARD_LABELS.DAYS(cycle?.days_to_close ?? 0),
              Icon: Clock,
            },
            {
              label: DASHBOARD_LABELS.PROCESSED_IN_CYCLE,
              value: DASHBOARD_LABELS.CASES(processed),
              highlight: DASHBOARD_LABELS.APPROVED_PERCENTAGE(
                cycle?.approved_percentage ?? 0
              ),
              highlightClassName: APPROVED_HIGHLIGHT_CLASS_NAME,
              Icon: CheckCircle2,
            },
            {
              label: DASHBOARD_LABELS.VS_PREVIOUS_CYCLE,
              value: DASHBOARD_LABELS.CASES_DIFF(casesDiff),
              valueClassName: CASES_DIFF_CLASS_NAME,
              note: DASHBOARD_LABELS.PREVIOUS_BASELINE(
                previousProcessed
              ),
              noteTitle: DASHBOARD_LABELS.COMPARISON_TOOLTIP(
                cycle?.previous_cycle_interval ?? ""
              ),
              Icon: TrendingUp,
            },
          ]}
        />

        <div className="flex items-center justify-end gap-3">
          <ExportTablesMenu
            items={exports.rangeItems}
            consolidatedItems={exports.consolidatedItems}
          />
        </div>

        <SummaryCards range={range} />
        <PendingActions />
        <ShippingHistory range={range} />
      </ContentContainer>
    </PageLayout>
  );
};

export default DashboardConciliacao;
